import React from 'react';
import usePoses from '../../../hooks/usePoses';
import useBackgrounds from '../../../hooks/useBackgrounds';
import usePricelist from '../../../hooks/usePricelist';
import useMultiSeasonPoses from '../../../hooks/useMultiSeasonPoses';
import useImageOrientations from '../../../hooks/useImageOrientations';
import { ORIENTATION } from '../../../hooks/useImageOrientation';
import usePackagePoses from '../../../hooks/usePackagePoses';
import watermarkImage from './Watermark_v2.png';
import horizWatermarkImage from './Horiz_Grad_Watermark.png';
// eslint-disable-next-line no-unused-vars
import extrastyles from './AbsolutePosImgs.scss';
import {
  LAYOUTS, BG_SUBSTRINGS, selectLayout, withFreeMagnets, BUY_ALL_TILE_LAYOUT, namedLayoutFor,
} from './packageLayouts';

// The customer-facing package-preview compositor. Given the student's poses and a
// package option, it shows "what this package looks like" by absolutely
// positioning the student's photos over backgrounds with a watermark.
//
// This used to be a single 7,500-line component where every package/option's
// layout was hand-coded. The layouts now live as data in packageLayouts.js
// (selectLayout + LAYOUTS); this file only does the runtime work that genuinely
// depends on the student's live data: resolving pose ROLES and background TAGS to
// real image URLs. See packageLayouts.test.js for the parity guarantee.
//
// Note: called as a plain function — AbsolutePosImgs(customer, packageId, option)
// — from PackageDisplay, so its hooks run within PackageDisplay's render.
//
// Optional 4th argument (issue #5218):
//   freeMagnets  add the offer's magnet slot (withFreeMagnets) while the
//                free-magnets tier is live -- the child's face on the magnet
//   layoutKey    render a NAMED layout instead of selectLayout's choice
//                ('buyAllTile' for the gallery's Download-all card)
//
// Product FRAMES: a slot with `frame` ('magnet' | 'phone') gets a CSS product
// mock drawn around the same pose image -- no image processing, no extra asset.

const isHorizontal = (url) => url.includes('.jpg') || url.includes('-23.png');
// Graphic renders (framed-room mockups, promo graphics) carry a G pose number,
// e.g. ...-G02.png. They're products, not portraits — never preview material.
const isGraphic = (url) => /-g\d+\./i.test(url);
const randomIndex = (arr) => (arr && arr.length ? Math.floor(Math.random() * arr.length) : 0);
const pick = (subset, fallback) => (
  subset.length ? subset[randomIndex(subset)].imageUrl : fallback[randomIndex(fallback)].imageUrl
);

const NAMED_LAYOUTS = { buyAllTile: BUY_ALL_TILE_LAYOUT };

// A fridge magnet: white bevel, soft shadow, a small FREE tag. Sized by the
// slot's width like every other layer; the pose img below carries the 4:5 box.
const MAGNET_FRAME_STYLE = {
  boxSizing: 'border-box',
  border: '3px solid #fff',
  borderRadius: '10%',
  boxShadow: '0 3px 8px rgba(0,0,0,0.35)',
  overflow: 'hidden',
  background: '#fff',
};
const MAGNET_TAG_STYLE = {
  position: 'absolute',
  top: '-9%',
  right: '-6%',
  padding: '1px 6px',
  fontSize: '9px',
  lineHeight: '14px',
  fontWeight: 800,
  letterSpacing: '0.04em',
  color: '#fff',
  background: '#d93a3a',
  borderRadius: '9px',
  zIndex: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
};
// A phone: dark rounded bezel with the pose as the screen.
const PHONE_FRAME_STYLE = {
  boxSizing: 'border-box',
  border: '4px solid #1c1c1e',
  borderRadius: '14%/9%',
  boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
  overflow: 'hidden',
  background: '#1c1c1e',
};

// Wrap a slot's layers in its product frame. Position/size move from the layers
// to the frame; the layers fill it.
const framed = (frame, style, layers) => {
  const box = {
    position: style.position,
    top: style.top,
    left: style.left,
    width: style.width,
    zIndex: 1,
    aspectRatio: style.aspectRatio,
  };
  const fill = {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover',
  };
  const inner = React.Children.map(layers, (layer) => (
    layer ? React.cloneElement(layer, { style: fill }) : null
  ));
  if (frame === 'phone') return <div style={{ ...box, ...PHONE_FRAME_STYLE }} data-frame="phone">{inner}</div>;
  return (
    <div style={box} data-frame="magnet">
      <div style={{ ...MAGNET_FRAME_STYLE, position: 'absolute', inset: 0 }}>{inner}</div>
      <span style={MAGNET_TAG_STYLE}>FREE</span>
    </div>
  );
};

const AbsolutePosImgs = (customer, packageId, option, { freeMagnets = false, layoutKey = null } = {}) => {
  const { currentStudent } = customer;
  const { data: pricelist } = usePricelist(packageId);
  // Multi-season poses when the pricelist spans seasons / shares poses, else this season's.
  const { data: poses } = pricelist?.id && (pricelist?.multipleSeasons?.length || pricelist?.displaySharedPoses)
    ? useMultiSeasonPoses(currentStudent.id, packageId)
    : usePoses(currentStudent.id, packageId);
  // selectLayout is pure, so the layout is known before any gating — and only
  // layouts that actually place a background layer need the school's background
  // set measured (or waited for) at all.
  const baseLayout = layoutKey
    ? namedLayoutFor(layoutKey, freeMagnets, NAMED_LAYOUTS)
    : LAYOUTS[selectLayout(packageId, option.name)];
  const layout = freeMagnets && baseLayout && !layoutKey ? withFreeMagnets(baseLayout) : baseLayout;
  const needsBackgrounds = !!layout && layout.some((slot) => slot && slot.background);
  const { data: backgrounds, isPlaceholderData: backgroundsPending } = useBackgrounds(packageId);
  const orientations = useImageOrientations([
    ...(poses || []).map((p) => p.imageUrl),
    ...(needsBackgrounds ? (backgrounds || []).map((b) => b.imageUrl) : []),
  ]);
  const { data: packagePoses, isPlaceholderData: packagePosesPending } = usePackagePoses(packageId);

  // Don't render until poses are available, or if every pose is a horizontal .jpg.
  if (poses && poses.length > 0 && poses.every((p) => p.imageUrl && p.imageUrl.includes('.jpg'))) return null;
  if (!poses || poses.length === 0) return null;

  if (!layout) return null;

  // Wait for every pose's (and, where the layout uses them, background's) real
  // orientation — measured from the loaded image, because filename conventions
  // for "which images are horizontal" drift every season — and for the
  // pricelist's Poses list, so the preview renders once, with the right pool.
  // Failed loads resolve to UNKNOWN, so this can't hang the preview.
  const orientationOf = (item) => (orientations[item.imageUrl] || {}).orientation;
  if (!poses.every((p) => !p.imageUrl || orientationOf(p))) return null;
  if (needsBackgrounds) {
    if (backgroundsPending) return null;
    if (!(backgrounds || []).every((b) => !b.imageUrl || orientationOf(b))) return null;
  }
  if (packagePosesPending) return null;

  // --- Resolve a background TAG to a URL from the school's background set ---
  // (scans backgrounds[].imageUrl for the tag's substring; falls back to the
  // first). Only portrait-measured backgrounds qualify: school sets now include
  // horizontal backgrounds (the "…Horiz" ones paired with landscape poses), and
  // a wide background in a vertical print slot is what broke the cards. No
  // portrait background at all → render the slot with no bg layer.
  const portraitBackgrounds = (backgrounds || [])
    .filter((b) => b.imageUrl && orientationOf(b) === ORIENTATION.PORTRAIT);
  const resolveBackground = (tag) => {
    if (!tag) return null;
    const needle = BG_SUBSTRINGS[tag];
    const hit = portraitBackgrounds.find((b) => b.imageUrl.includes(needle));
    if (hit) return hit.imageUrl;
    return portraitBackgrounds[0] ? portraitBackgrounds[0].imageUrl : null;
  };

  // --- Resolve pose ROLES to URLs from the student's live poses ---
  // The preview pool is the pricelist's admin-curated Poses list (package_poses,
  // in the admin's drag order), resolved against the student's gallery the same
  // way the multi-pose Builder does: match on pose number, plus season for
  // multi-season pricelists. Horizontal (landscape-measured) images and graphic
  // renders are never used — the print slots are vertical, and the designer
  // "looks" include landscape renders with school/name/year text baked in that
  // broke the previews. Falls back to the student's portraits when the pricelist
  // has no Poses configured (or none match this student).
  const candidates = poses.filter((p) => p.imageUrl && !isGraphic(p.imageUrl));
  // Measured-portrait only: UNKNOWN (probe failed) is excluded — fail closed,
  // never risk a landscape or graphic render in a vertical print slot.
  const portraits = candidates.filter((p) => orientationOf(p) === ORIENTATION.PORTRAIT);
  // No usable portrait at all? Show the bare card art rather than falling back
  // to the unfiltered gallery — that fallback was this bug's escape hatch.
  if (portraits.length === 0) return null;

  const matchesSeason = (packagePose, pose) => (
    !pricelist?.multipleSeasons?.length
    || (packagePose.season || '').toLowerCase() === (pose.folder || '').toLowerCase()
  );
  const pricelistPortraits = (packagePoses || [])
    .map((pp) => portraits.find((p) => pp.poseNumber === p.number && matchesSeason(pp, p)))
    .filter(Boolean)
    .filter((p, i, arr) => arr.indexOf(p) === i);
  const pool = pricelistPortraits.length > 0 ? pricelistPortraits : portraits;
  const pose1 = pool[0].imageUrl;

  const roles = {
    pose1,
    pose2: pick(pool, portraits),
    pose3: pick(pool, portraits),
    pose4: pick(pool, portraits),
  };

  // poseHoriz: the wide chalkboard slot (picturePerfect / allDigital) takes a LANDSCAPE
  // pose — for pricelist 778 those are poses 10-13. `portraits` filters landscapes out of
  // the vertical print slots; the horizontal slot is the one place they belong. Scope the
  // landscape to the pricelist (package_poses) the SAME way pricelistPortraits is: for
  // senior / multi-season packages `candidates` is the WHOLE gallery (useMultiSeasonPoses),
  // so an unscoped first-landscape would grab an off-pricelist designer look (e.g. a
  // school-name chalkboard render). Fall back to unscoped landscapes only when the
  // pricelist configures no poses at all. Empty when the student has no landscape frame —
  // never a portrait in the wide slot, which letterboxed badly.
  const isLandscapePose = (p) => orientationOf(p) === ORIENTATION.LANDSCAPE;
  const pricelistLandscapes = (packagePoses || [])
    .map((pp) => candidates.find((p) => pp.poseNumber === p.number && matchesSeason(pp, p) && isLandscapePose(p)))
    .filter(Boolean)
    .filter((p, i, arr) => arr.indexOf(p) === i);
  const landscapePool = (packagePoses || []).length > 0
    ? pricelistLandscapes
    : candidates.filter(isLandscapePose);
  roles.poseHoriz = landscapePool.length > 0 ? landscapePool[0].imageUrl : null;

  // Package 610 uses spring-aware pose variants.
  if (Number(packageId) === 610) {
    const nonSpring = poses.filter((p) => !isHorizontal(p.imageUrl) && !p.imageUrl.includes('-spr0'));
    const nonE0 = poses.filter((p) => !isHorizontal(p.imageUrl) && !p.imageUrl.includes('-e0'));
    roles.pose2 = pick(nonSpring, poses);
    roles.pose3 = pick(nonSpring, poses);
    roles.springpose1 = pick(nonE0, poses);
    roles.springpose2 = pick(nonE0, poses);
  }

  return (
    <div>
      <div className="image-container">
        {layout.map((slot, i) => {
          if (!slot) return null; // hidden slot
          const poseUrl = roles[slot.pose];
          if (!poseUrl) return null;
          // Bound each layer to the pose image's own aspect family — pngmed
          // portraits are 640x800 (4:5) and horizontals 800x640 (5:4) — so
          // standard assets render pixel-identical to natural sizing while a
          // malformed file can't explode the layout. Residual overflow (e.g. a
          // portrait fallback in the wide poseHoriz slot) is clipped at the
          // card-art edge by .upperContainer's overflow: hidden.
          const poseOrientation = (orientations[poseUrl] || {}).orientation;
          const style = {
            position: 'absolute',
            top: slot.top,
            left: slot.left,
            width: slot.width,
            zIndex: 0,
            // A slot may pin its own aspect (the Buy All tile's phone screen is
            // taller than 4:5); otherwise portrait/landscape as measured.
            aspectRatio: slot.aspectRatio || (poseOrientation === ORIENTATION.LANDSCAPE ? '5 / 4' : '4 / 5'),
            objectFit: 'contain',
          };
          const backgroundUrl = resolveBackground(slot.background);
          // Landscape poses (the class photo / 10-13) get the landscape watermark — keyed off
          // the MEASURED orientation, not the extension (those frames are .png now, not .jpg).
          const watermark = poseOrientation === ORIENTATION.LANDSCAPE ? horizWatermarkImage : watermarkImage;
          const layers = [
            backgroundUrl ? <img key="bg" src={backgroundUrl} alt="" style={style} /> : null,
            <img key="pose" src={poseUrl} alt="" style={style} />,
            <img key="wm" src={watermark} alt="Watermark" style={style} />,
          ];
          return (
            // Slots are a fixed positional array, so the index is a stable key.
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={i}>
              {slot.frame ? framed(slot.frame, style, layers) : layers}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default AbsolutePosImgs;
